import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Pause, RotateCcw, Loader2 } from "lucide-react";
import { PageContainer, PageHeader, Pill } from "@/components/layout/PageShell";
import { DataToolbar, DataTable } from "@/components/layout/DataTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";

const ORDER_TONES = {
  DI: "blue",
  TA: "amber",
  DL: "purple",
};

function formatHeldAt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function HoldBills() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["held-bills", branchId, search],
    queryFn: async () => {
      const { data: res } = await api.get("/held-bills", {
        params: { branch_id: branchId, search: search || undefined },
      });
      return res;
    },
    enabled: Boolean(branchId),
  });

  const recallMutation = useMutation({
    mutationFn: async (billId) => {
      const { data: res } = await api.post(
        `/bills/${billId}/recall`,
        {},
        { params: { branch_id: branchId } },
      );
      return res;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["held-bills", branchId] });
      toast.success(`Bill ${res.bill.bill_number} recalled`);
      navigate("/pos", { state: { recalledBill: res.bill } });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to recall bill");
    },
  });

  const heldBills = data?.held_bills ?? [];
  const holdEnabled = data?.hold_enabled !== false;

  const columns = [
    {
      key: "bill_number",
      label: "Bill #",
      render: (_, row) => (
        <span className="font-mono font-bold text-blue-700">{row.bill_number}</span>
      ),
    },
    {
      key: "sales_no",
      label: "Sales No",
      mono: true,
    },
    {
      key: "order_type_label",
      label: "Type",
      render: (_, row) => (
        <Pill tone={ORDER_TONES[row.order_type] || "slate"}>{row.order_type_label}</Pill>
      ),
    },
    {
      key: "customer_name",
      label: "Customer",
      bold: true,
    },
    {
      key: "table_code",
      label: "Table",
      render: (value) => value || "—",
    },
    {
      key: "line_count",
      label: "Items",
      mono: true,
    },
    {
      key: "net_amount",
      label: "Total",
      align: "right",
      render: (_, row) => (
        <span className="font-mono font-semibold">
          {money(row.net_amount, row.currency_code)}
        </span>
      ),
    },
    {
      key: "held_at",
      label: "Held At",
      render: (value) => (
        <span className="text-slate-600 text-[12.5px]">{formatHeldAt(value)}</span>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Held Bills"
        subtitle="Bills parked from POS — recall to continue billing"
        breadcrumbBadge={
          !holdEnabled ? <Pill tone="red">Hold disabled in settings</Pill> : null
        }
        actions={
          <Button
            size="sm"
            className="h-9 bg-[#2563EB] hover:bg-blue-700"
            onClick={() => navigate("/pos")}
            data-testid="held-bills-open-pos"
          >
            <Pause size={14} className="mr-1.5" /> Open POS
          </Button>
        }
      />

      <DataToolbar
        searchValue={search}
        onSearch={setSearch}
        testIdPrefix="held-bills"
      />

      {isLoading ? (
        <div className="h-32 grid place-items-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={heldBills}
          emptyText="No held bills — use HOLD (F1) in POS to park a bill"
          testIdPrefix="held-bills"
          onRowClick={(row) => recallMutation.mutate(row.id)}
          rowActions={(row) => [
            {
              title: "Recall",
              icon: recallMutation.isPending && recallMutation.variables === row.id
                ? <Loader2 size={14} className="animate-spin" />
                : <RotateCcw size={14} />,
              onClick: () => recallMutation.mutate(row.id),
            },
          ]}
        />
      )}
    </PageContainer>
  );
}
